export const truncateName = (name: string, toLegth: number) => {
  return name.length > toLegth ? `${name.substring(0, toLegth)}...` : name;
};

export function formatNumber(numberString) {
  // Remove extra decimal point
  const [integerPart, decimalPart] = numberString.split(".");
  const formattedNumberString = `${integerPart}.${decimalPart.slice(0, 2)}`;

  // Convert to number and format with two decimal places
  const formattedNumber = parseFloat(formattedNumberString).toFixed(2);

  return formattedNumber;
}

export const formatDate = (dateString: Date) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const safeNumberConversion = (value: any): number | null => {
  // Check if value is a valid number or can be converted to a valid number
  const num = Number(value);
  return !isNaN(num) && value !== null && value !== undefined ? num : null;
};
export const capitalizeFirstLetter = (str: string | undefined | null) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};
export const formatDateToShowTime = (dateString: Date) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true, // This makes sure it displays AM/PM
  });
};

export const formatDate2 = (dateInput?: string | Date | null) => {
  if (!dateInput) return "";

  if (dateInput instanceof Date) {
    if (isNaN(dateInput.getTime())) return "";
    const year = dateInput.getFullYear();
    const month = String(dateInput.getMonth() + 1).padStart(2, "0");
    const day = String(dateInput.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const isoMatch = dateInput.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];

  const parsed = new Date(dateInput);
  if (!isNaN(parsed.getTime())) {
    return formatDate2(parsed);
  }

  return dateInput.substring(0, 10);
};

export const formatDateTime = (date: Date | string) => {
  if (!date) return "";

  // If date is already a string in the "YYYY-MM-DDTHH:mm" format, return it directly.
  // Otherwise, if it's a Date object, format it.
  if (typeof date === "string") {
    return date;
  }

  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => n.toString().padStart(2, "0");
  const yyyy = d.getFullYear();
  const MM = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
};

export const convertToRGBA = (hexColor: string) => {
  const alpha = parseInt(hexColor.substring(1, 3), 16) / 255; // Convert AA to opacity (0-1)
  const r = parseInt(hexColor.substring(3, 5), 16);
  const g = parseInt(hexColor.substring(5, 7), 16);
  const b = parseInt(hexColor.substring(7, 9), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Converts #AARRGGBB to rgba; passes #RRGGBB through unchanged. */
export const toDisplayStatusColor = (hexColor: string): string => {
  if (!hexColor?.startsWith("#")) {
    return hexColor;
  }
  if (/^#[0-9A-Fa-f]{8}$/.test(hexColor)) {
    return convertToRGBA(hexColor);
  }
  return hexColor;
};

export const lightenRGBColor = (argb: string, lightnessFactor: number) => {
  // Ensure it's a valid 8-character hex code
  if (!/^#([A-Fa-f0-9]{8})$/.test(argb)) return argb;

  // Extract ARGB components
  const alpha = parseInt(argb.substring(1, 3), 16) / 255; // Convert AA (0-255) to 0-1
  const r = parseInt(argb.substring(3, 5), 16);
  const g = parseInt(argb.substring(5, 7), 16);
  const b = parseInt(argb.substring(7, 9), 16);

  // Convert to HSL
  let { h, s, l } = rgbToHsl(r, g, b);

  // Lighten the color
  l = Math.min(1, l + lightnessFactor);

  // Convert back to RGB
  const { r: newR, g: newG, b: newB } = hslToRgb(h, s, l);

  // Convert to RGBA
  return `rgba(${newR}, ${newG}, ${newB}, ${alpha.toFixed(2)})`;
};

// RGB to HSL
const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return { h, s, l };
};

// HSL to RGB
const hslToRgb = (h: number, s: number, l: number) => {
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
};
// if db or app doesnt' work with UTC then save the date as string instead of date format
export const getLocalTime = (): string => {
  const now = new Date();

  const pad = (n: number) => n.toString().padStart(2, "0");

  const year = now.getFullYear();
  const month = pad(now.getMonth() + 1);
  const day = pad(now.getDate());
  const hours = pad(now.getHours());
  const minutes = pad(now.getMinutes());
  const seconds = pad(now.getSeconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};
